/**
 * Shared data formatting utilities for SmartDataView and ArtifactPanel.
 * Handles field name formatting, value display, and type-specific rendering.
 */

/** Format field name: snake_case or dot.notation to Title Case */
export function formatFieldName(key: string): string {
  return key
    .split(/[._]/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

/** Format a value for table cell display */
export function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '-'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'number') {
    if (Math.abs(value) < 0.0001 && value !== 0) {
      return value.toExponential(4)
    }
    return value.toLocaleString('en-US', { maximumFractionDigits: 6 })
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return '(empty)'
    if (typeof value[0] !== 'object') {
      return value.join(', ')
    }
    return `[${value.length} items]`
  }
  if (typeof value === 'object') {
    return JSON.stringify(value)
  }
  // Check if it looks like a date
  const strValue = String(value)
  if (/^\d{4}-\d{2}-\d{2}/.test(strValue)) {
    try {
      const date = new Date(strValue)
      if (!isNaN(date.getTime())) {
        return date.toLocaleString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        })
      }
    } catch {
      // Not a valid date
    }
  }
  return strValue
}

/** Classify a value for rendering decisions */
export type ValueKind =
  | 'primitive'
  | 'simple-object'     // object with <=5 keys
  | 'complex-object'    // object with >5 keys
  | 'array-of-objects'  // array where first element is object
  | 'array-of-arrays'   // array where first element is array
  | 'array-of-primitives'
  | 'mixed-array'
  | 'empty'
  | 'null'

export function classifyValue(value: unknown): ValueKind {
  if (value === null || value === undefined) return 'null'
  if (Array.isArray(value)) {
    if (value.length === 0) return 'empty'
    const hasObjects = value.some((v) => typeof v === 'object' && v !== null && !Array.isArray(v))
    const hasArrays = value.some((v) => Array.isArray(v))
    const hasPrimitives = value.some((v) => typeof v !== 'object' || v === null)
    if (hasArrays && !hasObjects && !hasPrimitives) return 'array-of-arrays'
    if (hasObjects && !hasPrimitives && !hasArrays) return 'array-of-objects'
    if (!hasObjects && !hasArrays) return 'array-of-primitives'
    return 'mixed-array'
  }
  if (typeof value === 'object') {
    const keys = Object.keys(value as object)
    if (keys.length === 0) return 'empty'
    return keys.length <= 5 ? 'simple-object' : 'complex-object'
  }
  return 'primitive'
}

/** Max string length before truncation */
export const MAX_STRING_LENGTH = 200

/** Page size for large key-value tables */
export const PAGE_SIZE = 20
